import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useMembership } from "@/hooks/useMembership";
import { getOnboardingState, type OnboardingState } from "@/lib/onboarding.functions";
import { useTherapistOnlyMode } from "@/lib/launch-mode";
import { DashboardHome } from "@/components/dashboard/DashboardHome";
import { FirstSessionOnboarding } from "@/components/dashboard/FirstSessionOnboarding";
import { MemberFeedbackPrompt } from "@/components/feedback/MemberFeedbackPrompt";
import { refreshMembership } from "@/lib/membership-refresh";

import { ReferralSourcePrompt } from "@/components/ReferralSourcePrompt";

import { trackDashboardOpened, trackMembershipJourneyCompleted } from "@/lib/member-journey";


/**
 * /dashboard — the calm home for signed-in members.
 *
 * Gate policy (soft, non-blocking):
 *   - Not signed in            → send to /auth
 *   - Signed-in + paid, but pre-launch → send to /youre-in
 *   - Everyone else signed in   → render <DashboardHome />
 *     (unpaid users see the "Complete your Membership" banner at
 *     the top via <DashboardStatusBanner />, but are NOT redirected — they
 *     can still browse the calm dashboard.)
 *
 * Admins always pass.
 */

export const Route = createFileRoute("/dashboard")({
  head: () => ({
    meta: [
      { title: "Your Dashboard — Recovery Trauma™" },
      { property: "og:title", content: "Your Dashboard — Recovery Trauma™" },
      { property: "og:description", content: "Your personal recovery space, saved progress and gentle daily support." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      {
        name: "description",
        content:
          "A calm starting point for today: an affirmation, a next step, and quick access to your recovery tools.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DashboardRoute,
});

function DashboardRoute() {
  const membership = useMembership();
  const navigate = useNavigate();
  const fetchState = useServerFn(getOnboardingState);
  const preLaunch = useTherapistOnlyMode();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [checked, setChecked] = useState(false);

  // Journey tracking: record that this member opened their dashboard, and
  // whether their membership is already complete.
  useEffect(() => {
    if (membership.loading || !membership.isAuthed) return;
    trackDashboardOpened({ is_member: membership.isMember });
    if (membership.isMember) trackMembershipJourneyCompleted();
  }, [membership.loading, membership.isAuthed, membership.isMember]);

  // Redirect guests to sign-in. `hydrated` guards the first client render,
  // which deliberately matches the server's signed-out snapshot — without it
  // a signed-in member is bounced to /auth on mount.
  useEffect(() => {
    if (membership.loading) return;
    // Only when auth has definitively confirmed there is no session.
    if (membership.canRedirectToSignIn) {
      navigate({ to: "/auth", search: { redirect: "/dashboard" } as never, replace: true });
    }
  }, [membership.loading, membership.canRedirectToSignIn, navigate]);

  // Load onboarding state so we can decide whether a paid member should
  // be routed to /youre-in during pre-launch. Non-members simply fall
  // through to the calm dashboard with the soft banner.
  useEffect(() => {
    let alive = true;
    if (!membership.isAuthed || membership.loading) return;
    if (membership.isAdmin) {
      setChecked(true);
      return;
    }
    fetchState({ data: undefined as never })
      .then((s) => {
        if (!alive) return;
        setState(s);
        setChecked(true);
        if (s.is_member && !membership.isMember) refreshMembership();
      })
      .catch(() => {
        if (!alive) return;
        setChecked(true);
      });
    return () => {
      alive = false;
    };
  }, [membership.isAuthed, membership.loading, membership.isAdmin, membership.isMember, fetchState]);


  // Route based on server-side truth:
  //   - unpaid              → /complete-membership
  //   - paid pre-launch     → /youre-in
  //   - paid post-launch    → stay on dashboard
  // Email confirmation is deliberately not a gate.
  useEffect(() => {
    if (!checked) return;
    if (membership.isAdmin) return;
    if (!state) return;
    if (!state.is_member) {
      navigate({ to: "/complete-membership", replace: true });
      return;
    }

    if (preLaunch) {
      navigate({ to: "/youre-in", replace: true });
    }
  }, [checked, preLaunch, state, membership.isAdmin, navigate]);

  if (membership.loading || !membership.isAuthed) {
    return (
      <div className="flex min-h-[60svh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--gold-deep)]" />
      </div>
    );
  }

  // Wait for the state check to complete before rendering the dashboard —
  // otherwise unpaid or paid-pre-launch users briefly see DashboardHome
  // for a frame before being redirected. Admins skip this check.
  if (!membership.isAdmin && !checked) {
    return (
      <div className="flex min-h-[60svh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--gold-deep)]" />
      </div>
    );
  }

  // Anyone the effect above will redirect — hold the spinner rather than
  // flashing DashboardHome for a frame.
  if (
    !membership.isAdmin &&
    state &&
    (!state.is_member || preLaunch)
  ) {

    return (
      <div className="flex min-h-[60svh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--gold-deep)]" />
      </div>
    );
  }

  return (
    <>
      {/* First-session guide — only for a member who hasn't finished onboarding. */}
      <div className="mx-auto w-full max-w-5xl px-4 pt-4">
        <FirstSessionOnboarding
          firstName={membership.displayName && membership.displayName !== "friend" ? membership.displayName : null}
          show={!membership.isAdmin && !!state?.is_member && state?.step !== "done"}
        />
      </div>
      {/* Short optional "how did you find us?" question, once, if unanswered. */}
      <div className="mx-auto w-full max-w-5xl px-4 pt-4">
        <ReferralSourcePrompt />
      </div>
      <DashboardHome />
      <MemberFeedbackPrompt />

    </>
  );
}

